<template>
    <div class="group-input-array"
         :class="$attrs.class"
         v-for="(item, i) in (modelValue || [])"
         :key="'group-input-' + i"
    >
        <FieldGroup_input
            class="group-input-array-id flex-1 mr__8"
            v-if="editId && itemType === 'object'"
            :id="'group-input-id-' + (index != null ? index + '-' : '') + i"
            :label="'ID'"
            :placeholder="'ID'"
            :error="getError(i, this.field_id)"
            :disabled="disabled"
            :modelValue="item?.[field_id] ?? ''"
            @update:modelValue="onInputId(i, $event)"
        />
        <div class="flex flex-1">
            <FieldGroup_input
                :id="'group-input-' + (index != null ? index + '-' : '') + i"
                class="group-input-array-value flex-1"
                :label="getLabel(item, i)"
                :placeholder="placeholder"
                :error="getError(i, this.field_value)"
                :disabled="disabled"
                :modelValue="getInputValue(item)"
                @update:modelValue="onInput(i, $event)"
            />
            <template v-if="!disabled && !noButtons">
                <button class="button button_view_icon button_theme_base ml__8"
                        v-if="i === (modelValue || []).length - 1 && (maxItems == null || maxItems <= 0 || i < maxItems - 1)"
                        :disabled="!getInputValue(item)"
                        @click="addValue()"
                >
                    <span class="icon"> <span class="icon__symbol"><SvgIcon class="icon" name="common__add_lg"/></span> </span>
                </button>
                <button class="button button_view_icon button_theme_base is_danger ml__8"
                        @click="removeValue(i)"
                >
                    <span class="icon"><span class="icon__symbol"><SvgIcon class="icon" name="common__delete_lg"/></span> </span>
                </button>
            </template>
        </div>
    </div>
    <button class="button button_view_base button_theme_neutral group-input-array-button"
            v-if="!disabled && !noAddButton && (!modelValue || !modelValue.length)"
            @click="addValue()"
    >
        {{ $t('text.Add') }} {{ label }}
    </button>
</template>

<script>
/**
 * FieldGroup_array_inputs — renders an array of inputs with add/remove buttons
 *
 * Usage examples:
 *
 * 1. Array of strings (default mode):
 *    <FieldGroup_array_inputs v-model="['a', 'b']" label="Email" />
 *    → modelValue: ['a', 'b']
 *
 * 2. Array of objects (single field editing):
 *    <FieldGroup_array_inputs
 *        v-model="[{name:'Ivan'}]"
 *        item-type="object"
 *        field_value="name"
 *        label="User"
 *    />
 *    → modelValue: [{name:'Ivan'}]
 *
 * 3. Array of objects (label from object field):
 *    <FieldGroup_array_inputs
 *        v-model="[{name:'Ivan', title:'Manager'}]"
 *        item-type="object"
 *        field_value="name"
 *        field_name="title"
 *        label="User"
 *    />
 *    → modelValue: [{name:'Ivan', title:'Manager'}]
 *
 * 4. Array of objects (editable ID field):
 *    <FieldGroup_array_inputs
 *        v-model="[{id:1, name:'Ivan'}]"
 *        item-type="object"
 *        field_value="name"
 *        :edit-id="true"
 *        label="User"
 *    />
 *    → modelValue: [{id:1, name:'Ivan'}]
 *
 * 5. Custom ID field name:
 *    <FieldGroup_array_inputs
 *        v-model="[{camera_id:5, name:'Camera'}]"
 *        item-type="object"
 *        field_value="name"
 *        field_id="camera_id"
 *        :edit-id="true"
 *    />
 *    → modelValue: [{camera_id:5, name:'Camera'}]
 *
 * Props:
 *    modelValue   — Array, array of strings or objects (default [])
 *    itemType     — 'string' | 'object', mode (default 'string')
 *    field_value  — String, object key for value editing (objects only)
 *    field_name   — String, object key for label display (objects, optional)
 *    field_id     — String, object key for ID field (default 'id', used with editId)
 *    editId       — Boolean, show ID input field (default false)
 *    noButtons    — Boolean, hide add/remove buttons (default false)
 *    noAddButton  — Boolean, hide the big add button when array is empty (default false)
 *    maxItems     — Number, max items limit (0 or undefined = no limit)
 *    viewIndex    — Boolean, append index to label (#1, #2...)
 *    errors       — Array<string> | Array<{[field]: string}>
 *    label        — String, base label text
 *    placeholder  — String, input placeholder
 *    disabled     — Boolean, disable all inputs
 */
export default {
    name: "FieldGroup_array_inputs",
    emits: ['update:modelValue', 'actionAdd', 'actionRemove'],
    props: {
        modelValue: {
            type: Array,
            default: () => []
        },
        errors: Array,
        index: [String, Number],
        viewIndex: {
            type: Boolean,
            default: false
        },
        placeholder: {
            type: String
        },
        label: {
            type: String,
            default: ''
        },
        disabled: {
            type: Boolean,
            default: false
        },
        noButtons: {
            type: Boolean,
            default: false
        },
        noAddButton: {
            type: Boolean,
            default: false
        },
        maxItems: {
            type: Number,
            default: undefined
        },
        itemType: {
            type: String,
            default: 'string',
            validator: (v) => ['string', 'object'].includes(v)
        },
        field_value: {
            type: String,
            default: ''
        },
        field_name: {
            type: String,
            default: ''
        },
        field_id: {
            type: String,
            default: 'id'
        },
        editId: {
            type: Boolean,
            default: false
        },
    },
    methods: {
        getLabel(item, i) {
            const baseLabel = this.label + (this.viewIndex ? (' #' + (i + 1)) : '');
            if (this.editId) {
                return baseLabel;
            }
            if (this.itemType === 'object' && this.field_name) {
                return baseLabel + ' ' + (item[this.field_name] || '');
            }
            return baseLabel;
        },
        getError(i, field) {
            const err = (this.errors || [])[i];
            if (!err) return '';
            if (typeof err === 'object' && field) return err[field] || '';
            return err;
        },
        getInputValue(item) {
            if (this.itemType === 'object' && this.field_value) {
                return item?.[this.field_value] ?? '';
            }
            return item;
        },
        onInput(index, newValue) {
            const current = this.modelValue || [];
            const trimmed = typeof newValue === 'string' ? newValue.trim() : newValue;
            const arr = current.map((item, i) => {
                if (i === index) {
                    if (this.itemType === 'object' && this.field_value) {
                        return { ...item, [this.field_value]: trimmed };
                    }
                    return trimmed;
                }
                return item;
            });
            this.$emit('update:modelValue', arr);
        },
        onInputId(index, newValue) {
            const current = this.modelValue || [];
            const trimmed = typeof newValue === 'string' ? newValue.trim() : newValue;
            const arr = current.map((item, i) => {
                if (i === index) {
                    if (this.itemType === 'object') {
                        let val = trimmed;
                        if (trimmed !== '' && !isNaN(Number(trimmed)) && trimmed.trim() !== '') {
                            val = Number(trimmed);
                        }
                        return { ...item, [this.field_id]: val };
                    }
                }
                return item;
            });
            this.$emit('update:modelValue', arr);
        },
        removeValue(i) {
            const current = this.modelValue || [];
            let value = current.filter((_, idx) => idx !== i);

            this.$emit('update:modelValue', value);
            this.$emit('actionRemove');
        },
        addValue() {
            const current = this.modelValue || [];
            if (this.maxItems != null && this.maxItems > 0 && current.length >= this.maxItems) return;

            let newItem;
            if (this.itemType === 'object') {
                newItem = {
                    [this.field_value]: ''
                };
                if (this.field_name) {
                    newItem[this.field_name] = '';
                }
                if (this.editId) {
                    newItem[this.field_id] = current.length + 1;
                }
            } else {
                newItem = '';
            }

            this.$emit('update:modelValue', [...current, newItem]);
            this.$emit('actionAdd');
        },
    }
}
</script>

<style scoped lang="scss">
.group-input-array {
    display: flex;
    align-items: baseline;
}
.group-input-array-button {
    width: 100%;
}
</style>